/**
 * Video conversion utilities for cross-platform compatibility
 * Converts various video formats to MP4 for universal playback
 */

export interface VideoConversionResult {
  success: boolean;
  file?: File;
  error?: string;
  originalFormat?: string;
  convertedFormat?: string;
}

/**
 * Detects if a video file needs conversion to MP4
 */
export const needsVideoConversion = (file: File): boolean => {
  const fileName = file.name.toLowerCase();
  const mimeType = file.type.toLowerCase();
  
  // Check file extension
  const needsConversionByExtension = fileName.endsWith('.mov') || 
                                     fileName.endsWith('.avi') || 
                                     fileName.endsWith('.webm') ||
                                     fileName.endsWith('.mkv');
  
  // Check MIME type
  const needsConversionByMime = mimeType.includes('video/quicktime') ||
                               mimeType.includes('video/x-msvideo') ||
                               mimeType.includes('video/webm') ||
                               mimeType.includes('video/x-matroska');
  
  return needsConversionByExtension || needsConversionByMime;
};

/**
 * Converts a video file to MP4 format using browser APIs
 */
export const convertVideoToMP4 = async (file: File): Promise<VideoConversionResult> => {
  try {
    console.log('🎬 [VideoConverter] Starting video conversion:', {
      name: file.name,
      type: file.type,
      size: file.size
    });

    // Create video element to load the file
    const video = document.createElement('video');
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    
    if (!ctx) {
      throw new Error('Canvas context not available');
    }

    // Set up video element
    video.crossOrigin = 'anonymous';
    video.muted = true;
    video.playsInline = true;

    // Create a promise to handle video loading
    const videoLoadPromise = new Promise<void>((resolve, reject) => {
      video.onloadedmetadata = () => {
        console.log('🎬 [VideoConverter] Video metadata loaded:', {
          duration: video.duration,
          videoWidth: video.videoWidth,
          videoHeight: video.videoHeight
        });
        resolve();
      };
      
      video.onerror = (e) => {
        console.error('🎬 [VideoConverter] Video load error:', e);
        reject(new Error('Failed to load video for conversion'));
      };
      
      // Set timeout for video loading
      setTimeout(() => {
        reject(new Error('Video loading timeout'));
      }, 10000);
    });

    // Load the video
    video.src = URL.createObjectURL(file);
    await videoLoadPromise;

    // Set canvas dimensions to match video
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;

    // Create MediaRecorder to capture the video
    const stream = canvas.captureStream(30); // 30 FPS
    const mediaRecorder = new MediaRecorder(stream, {
      mimeType: 'video/mp4; codecs="avc1.42E01E"' // H.264 baseline profile
    });

    const chunks: Blob[] = [];
    
    mediaRecorder.ondataavailable = (event) => {
      if (event.data.size > 0) {
        chunks.push(event.data);
      }
    };

    const recordingPromise = new Promise<Blob>((resolve, reject) => {
      mediaRecorder.onstop = () => {
        const blob = new Blob(chunks, { type: 'video/mp4' });
        console.log('🎬 [VideoConverter] Recording stopped, blob size:', blob.size);
        resolve(blob);
      };
      
      mediaRecorder.onerror = (e) => {
        console.error('🎬 [VideoConverter] MediaRecorder error:', e);
        reject(new Error('MediaRecorder failed'));
      };
    });

    // Start recording
    mediaRecorder.start(100); // Record in 100ms chunks
    
    // Play the video and draw frames to canvas
    video.currentTime = 0;
    video.play();
    
    const drawFrame = () => {
      if (!video.paused && !video.ended) {
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
        requestAnimationFrame(drawFrame);
      }
    };
    
    drawFrame();
    
    // Wait for video to finish playing
    await new Promise<void>((resolve) => {
      video.onended = () => {
        console.log('🎬 [VideoConverter] Video playback ended');
        resolve();
      };
    });
    
    // Stop recording
    mediaRecorder.stop();
    const convertedBlob = await recordingPromise;
    
    // Create new File object with MP4 extension
    const convertedFileName = file.name.replace(/\.[^/.]+$/, '.mp4');
    const convertedFile = new File([convertedBlob], convertedFileName, {
      type: 'video/mp4',
      lastModified: Date.now()
    });

    console.log('✅ [VideoConverter] Video conversion successful:', {
      originalName: file.name,
      originalSize: file.size,
      convertedName: convertedFile.name,
      convertedSize: convertedFile.size,
      originalFormat: file.type,
      convertedFormat: convertedFile.type
    });

    // Clean up
    URL.revokeObjectURL(video.src);
    
    return {
      success: true,
      file: convertedFile,
      originalFormat: file.type,
      convertedFormat: convertedFile.type
    };

  } catch (error) {
    console.error('❌ [VideoConverter] Video conversion failed:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown conversion error',
      originalFormat: file.type
    };
  }
};

/**
 * Processes a file and converts it to MP4 if needed
 */
export const processVideoFile = async (file: File): Promise<VideoConversionResult> => {
  console.log('🎬 [VideoConverter] Processing file:', {
    name: file.name,
    type: file.type,
    size: file.size
  });

  // Check if conversion is needed
  if (!needsVideoConversion(file)) {
    console.log('✅ [VideoConverter] No conversion needed, file is already MP4 compatible');
    return {
      success: true,
      file: file,
      originalFormat: file.type,
      convertedFormat: file.type
    };
  }

  console.log('🔄 [VideoConverter] Conversion needed, starting conversion process...');
  return await convertVideoToMP4(file);
};
